import SunnyCloudyIcon from "@assets/svg/navigation/weather.svg"
import cloudCoverIcon from "@assets/svg/weather/cloudCover.svg"
import RainIcon from "@assets/svg/weather/rain.svg"
import snowIcon from "@assets/svg/weather/snow.svg"
import sunIcon from "@assets/svg/weather/sun.svg"
import DarkSunnyCloudyIcon from "@assets/png/weather_dark.png"
import DarkCloudCoverIcon from "@assets/png/cloudCover_dark.png"
import DarkRainIcon from "@assets/png/rain_dark.png"
import DarkSnowIcon from "@assets/png/snow_dark.png"
import DarkSunIcon from "@assets/png/sun_dark.png"
import {
	Autocomplete,
	Box,
	Button,
	Divider,
	Flex,
	Group,
	Image,
	Modal,
	Paper,
	Stack,
	Text,
	UnstyledButton
} from "@mantine/core"
import { useDebouncedValue, useDisclosure } from "@mantine/hooks"
import { changeLocationData } from "@services/authService"
import {
	fetchLocations,
	forecastValues,
	getForecast,
	getLocationData,
	getWeather,
	locationValue
} from "@services/weatherService"
import { IconCaretRightFilled } from "@tabler/icons-react"
import { WEATHER_DETAILS } from "@utils/constants.util"
import { matchSorter } from "match-sorter"
import { ReactElement, useEffect, useRef, useState } from "react"
import SevenDayForecast from "./SevenDayForecast"
import styles from "./WeatherWidget.module.css"
import { useAuthContext } from "../../../context/AuthProvider"
import { getLocation } from "@hooks/useLocation"
import { eventTrackingItems } from "@utils/eventTracking"
import { useMantineColorScheme } from "@mantine/core"

export interface WeatherObject {
	icon: ReactElement | string
	description: string
	temperature: string
	wind: string
}

export const getWeatherIcon = (
	weatherCode: number,
	colorScheme: "light" | "dark"
) => {
	const sunCodes = [113]
	const sunnyCloudycodes = [116]
	const cloudCoverCodes = [119, 122, 143, 248, 260]
	const rainCodes = [
		176, 182, 185, 200, 263, 266, 281, 284, 293, 296, 299, 302, 305, 308, 311,
		314, 317, 320, 350, 353, 356, 359, 362, 365, 374, 377, 386, 389
	]
	const snowCodes = [
		179, 227, 230, 323, 326, 329, 332, 335, 338, 368, 371, 392, 395
	]

	const weatherIcons = {
		light: {
			sun: sunIcon,
			cloudCover: cloudCoverIcon,
			rain: RainIcon,
			snow: snowIcon,
			sunnyCloudy: SunnyCloudyIcon
		},
		dark: {
			sun: DarkSunIcon,
			cloudCover: DarkCloudCoverIcon,
			rain: DarkRainIcon,
			snow: DarkSnowIcon,
			sunnyCloudy: DarkSunnyCloudyIcon
		}
	}

	if (sunCodes.includes(weatherCode)) {
		return weatherIcons[colorScheme].sun
	}
	if (sunnyCloudycodes.includes(weatherCode)) {
		return weatherIcons[colorScheme].sunnyCloudy
	}
	if (cloudCoverCodes.includes(weatherCode)) {
		return weatherIcons[colorScheme].cloudCover
	}
	if (rainCodes.includes(weatherCode)) {
		return weatherIcons[colorScheme].rain
	}
	if (snowCodes.includes(weatherCode)) {
		return weatherIcons[colorScheme].snow
	}
	return weatherIcons[colorScheme].sunnyCloudy // Default icon if no match found
}

export function WeatherWidget() {
	const { userProfile } = useAuthContext()
	const { colorScheme } = useMantineColorScheme()
	const [location, setLocation] = useState("-")
	const [weather, setWeather] = useState<WeatherObject>({
		icon: colorScheme === "dark" ? DarkSunnyCloudyIcon : SunnyCloudyIcon,
		description: WEATHER_DETAILS.description,
		temperature: WEATHER_DETAILS.temperature,
		wind: WEATHER_DETAILS.wind
	})
	const [locationOptions, setLocationOptions] = useState<string[]>([])
	const [temperature, setTemperature] = useState<string>("-")
	const [unit, setUnit] = useState("")
	const [forecastData, setForecastData] = useState<forecastValues[]>([])
	const [opened, { open, close }] = useDisclosure()
	const [locationData, setLocationData] = useState<locationValue | null>(null)
	const [wind, setWind] = useState("-")
	const [humidity, setHumidity] = useState("-")
	const [precipitation, setPrecipitation] = useState(0)
	const [forecastIconCode, setforecastIconCode] = useState<string>("")
	const [searchQuery, setSearchQuery] = useState("")
	const [latlong, setLatLong] = useState("")
	const inputRef = useRef<HTMLInputElement | null>(null)
	const [uniqueLocations, setUniqueLocations] = useState<getLocationData[]>([])
	const [debouncedSearchTerm] = useDebouncedValue(searchQuery, 300)
	const profileLocation = getLocation()

	useEffect(() => {
		defaultLocation()
	}, [userProfile])

	useEffect(() => {
		if (latlong) {
			fetchWeather(latlong, unit)
		}
	}, [latlong, unit])

	useEffect(() => {
		locationSearch(debouncedSearchTerm)
	}, [debouncedSearchTerm])

	const fetchWeather = async (latLongs: string, unit: string) => {
		if (!latLongs || latLongs === "," || !unit) return

		const weatherData = await getWeather(latLongs, unit)
		const forecastValues = await getForecast(latLongs, unit)

		const forecastDataIcon = forecastValues?.forecast.map(
			(forecastItem: forecastValues) => {
				if (forecastItem?.hourly.length > 0) {
					return forecastItem?.hourly[0]?.weatherCode
				}
				return undefined
			}
		)

		setForecastData(forecastValues?.forecast || [])

		const weatherIcon = getWeatherIcon(
			weatherData?.current?.weatherCode,
			colorScheme === "dark" ? "dark" : "light"
		)
		const forecastIcon = getWeatherIcon(
			forecastDataIcon[0] ?? 0,
			colorScheme === "dark" ? "dark" : "light"
		)

		setforecastIconCode(forecastIcon)

		const temperature = weatherData?.current?.temperature
			? unit === "m"
				? `${weatherData?.current?.temperature}°C`
				: `${weatherData?.current?.temperature}°F`
			: "-"

		setTemperature(temperature)

		const windSpeed =
			unit === "m"
				? `${weatherData?.current?.windSpeed} m/s`
				: `${Math.round(weatherData?.current?.windSpeed)} mph`

		const wind = `Wind: ${weatherData?.current?.windDir ?? "-"} ${windSpeed} / Hum: ${weatherData?.current?.humidity ?? "-"}%`
		weatherData?.location?.name &&
		weatherData?.location?.region &&
		weatherData?.location?.country
			? `${weatherData?.location?.name}, ${weatherData?.location?.region}, ${weatherData?.location?.country}`
			: "Unknown Location"

		const locationName =
			weatherData?.location?.name &&
			weatherData?.location?.region &&
			weatherData?.location?.country
				? `${weatherData?.location?.name}, ${weatherData?.location?.region}, ${weatherData?.location?.country}`
				: "Unknown Location"

		const newWeatherData = {
			icon: weatherIcon,
			description: weatherData?.current?.weatherDescriptions?.[0] ?? " ",
			temperature: temperature,
			wind: wind
		}
		const windData = `${weatherData?.current?.windDir} ${windSpeed}`
		setWind(windData)

		const humidityData = `${weatherData?.current?.humidity}%`
		setHumidity(humidityData)

		const precipitationData = weatherData?.current?.precip
		setPrecipitation(precipitationData)

		setWeather({
			icon: newWeatherData.icon,
			description: newWeatherData.description,
			temperature: newWeatherData.temperature,
			wind: newWeatherData.wind
		})

		setLocation(locationName)
		setLocationData(weatherData?.location)
	}

	const defaultLocation = async () => {
		try {
			if (userProfile) {
				const countryCode = userProfile?.countryCode

				countryCode === "USA" ? setUnit("f") : setUnit("m")

				const latLongs = `${profileLocation?.lat},${profileLocation?.long}`
				setLatLong(latLongs)
				fetchWeather(latLongs, unit)
			}
		} catch (_error) {
			throw new Error("Error fetching weather data.")
		}
	}

	const handleLocationSearch = async (value: string) => {
		setSearchQuery(value)
	}

	const locationSearch = async (value: string) => {
		if (!value.trim() || value.trim().length < 3) {
			setLocationOptions([])
			return
		}

		const changeLocation = await fetchLocations(value)

		if (!changeLocation || changeLocation.length === 0) {
			setLocationOptions(["No results found"])
		} else {
			// Create an array of location objects
			const allLocations = changeLocation.map((loc) => {
				const lat = loc?.lat
				const long = loc?.lon
				return {
					name: loc?.name,
					region: loc?.region,
					country: loc?.country,
					lat: lat,
					lon: long,
					fullName: `${loc?.name}, ${loc?.region}, ${loc?.country}`
				}
			})

			// Now we can find unique locations based on fullName
			const uniqueLocations = Array.from(
				new Set(allLocations.map((loc) => loc.fullName))
			)
				.map((fullName) => {
					return allLocations.find((loc) => loc.fullName === fullName)
				})
				.filter((loc): loc is getLocationData => loc !== undefined)

			setUniqueLocations(uniqueLocations)

			const filteredLocations = matchSorter(
				uniqueLocations.map((loc) => loc.fullName),
				value,
				{
					threshold: matchSorter.rankings.CONTAINS
				}
			)
			setLocationOptions(filteredLocations)
		}
	}
	const handleLocationChange = async (selectedLocation: string) => {
		const location = locationOptions.find(
			(option) => option === selectedLocation
		)
		eventTrackingItems({
			event_name: "search_bar",
			link_url: "",
			searchbarName: "Change Location",
			search_term: selectedLocation
		})
		if (location) {
			// Extract latitude and longitude from selected location
			const loc = uniqueLocations.find((loc) => loc?.fullName === location)
			if (loc) {
				const latlong = `${loc?.lat},${loc?.lon}`
				setLatLong(latlong)

				fetchWeather(latlong, unit)
				changeLocationData(userProfile, loc?.lon)
			}
		}
		handleWidgetToggle("", "Change Location")
	}

	const highlightText = (text: string, query: string) => {
		if (!text || typeof text !== "string" || !query) return text

		const regex = new RegExp(`(${query})`, "gi")
		const parts = text.split(regex)

		return parts.map((part, index) => {
			const isMatch = regex.test(part)
			return isMatch ? (
				<span key={index} className={styles.highlightText}>
					{part}
				</span>
			) : (
				part
			)
		})
	}

	const handleKeyDown = (location: string) => {
		if (location) {
			const selectedLocationObj = uniqueLocations.find(
				(loc) => loc?.fullName?.toLowerCase() === location?.toLowerCase()
			)

			if (selectedLocationObj) {
				const latlong = `${selectedLocationObj.lat},${selectedLocationObj.lon}`
				setLatLong(latlong)
				fetchWeather(latlong, unit)
			}
			handleWidgetToggle("", "Change Location")
		}
	}

	const handleFocus = () => {
		setSearchQuery("")
		setLocationOptions([])
	}

	const toggleUnit = (newUnit?: string) => {
		const finalUnit = newUnit ?? (unit === "m" ? "f" : "m")
		setUnit(finalUnit)

		if (latlong) {
			fetchWeather(latlong, finalUnit)
		} else {
			defaultLocation()
		}
	}

	const handleWidgetToggle = (action: "open" | "close" | "", title: string) => {
		action === "open" ? open() : close()
		const interactionType = action === "open" ? "Widget open" : "Widget close"
		eventTrackingItems({
			event_name: "navigation_click",
			navLinkText: title || "",
			interactionType: interactionType,
			category: "Header Menu click",
			link_url: "",
			event211: 1
		})
	}
	return (
		<>
			<Paper className={styles.weatherWidget} shadow="lg" p={30} radius="md">
				<Stack gap="lg">
					<Box>
						<Text
							fz={13}
							fw={500}
							lh={1.6}
							className={styles.weatherWidgetText}
						>
							Your Location
						</Text>
						<Text
							fz={18}
							fw={600}
							lh={1.2}
							className={styles.weatherWidgetText}
						>
							{location}
						</Text>
					</Box>
					<Group>
						<Image src={weather.icon} alt="Weather Icon" h={24} w={38} />
						<Box>
							<Text
								fz={13}
								fw={500}
								lh={1.6}
								className={styles.weatherWidgetText}
							>
								{weather.description}
							</Text>
							<Text
								fz={21}
								fw={500}
								lh={1}
								className={styles.weatherWidgetText}
							>
								{temperature}
							</Text>
							<Text fz={11} fw={500} className={styles.weatherWidgetText}>
								{weather.wind}
							</Text>
						</Box>
					</Group>
					<Group>
						<UnstyledButton
							fz={12}
							fw={600}
							lh={1.7}
							className={styles.weatherWidgetText}
							td="underline"
							tt="uppercase"
							onClick={() => {
								handleWidgetToggle("open", "7-Day Forecast")
								open()
							}}
						>
							7-Day Forecast
						</UnstyledButton>
					</Group>
					<Divider />
					<Flex gap="md" wrap="wrap">
						<Box className={styles.autoCompleteFlexWrapper}>
							<Autocomplete
								label="Change Location"
								placeholder="City, State/Province, Country"
								comboboxProps={{ withinPortal: false }}
								data={locationOptions}
								styles={{ dropdown: { top: 0, left: 0 } }}
								value={searchQuery}
								onChange={handleLocationSearch}
								variant="small-input"
								renderOption={(input) => {
									const { value } = input.option
									return (
										<div
											{...input}
											key={value}
											onClick={() => handleLocationChange(value)}
										>
											{highlightText(value || "", searchQuery)}
										</div>
									)
								}}
								classNames={{
									root: styles.autoCompleteFlexWrapper,
									label: styles.autoCompleteLabel,
									dropdown: styles.mantineAutocompleteDropdown,
									option: styles.autocompleteOption
								}}
								onFocus={handleFocus}
								onOptionSubmit={handleKeyDown}
								ref={inputRef}
							/>
						</Box>
						<Group mt="md" className={styles.h60}>
							<Button
								variant="filled"
								leftSection={<IconCaretRightFilled size={20} />}
								classNames={{
									root: styles.autocompleteButton,
									inner: styles.autocompleteInner,
									section: styles.mantineButtonSection
								}}
								onClick={() => handleLocationChange}
							/>
						</Group>
					</Flex>
				</Stack>
			</Paper>
			<Modal
				opened={opened}
				onClose={() => handleWidgetToggle("close", "7-Day Forrecast")}
				title={
					<Text fw={600} size="xl">
						7-Day FORECAST
					</Text>
				}
				size="xl"
				centered
				classNames={{
					body: styles.modalBody,
					title: styles.modalTitle
				}}
			>
				<SevenDayForecast
					forecast={forecastData}
					locationData={locationData}
					WindValues={wind}
					HumidityValue={humidity}
					precipitationValue={precipitation}
					weatherData={weather}
					forecastIcon={forecastIconCode}
					unit={unit}
					toggleUnit={toggleUnit}
				/>
			</Modal>
		</>
	)
}
